// <address-autocomplete> — Danish address lookup for a plain text input.
//
// Backed by DAWA, the Danish state's own address register
// (api.dataforsyningen.dk, Styrelsen for Dataforsyning og Infrastruktur). No key,
// no account, and Denmark-only by definition — which is exactly the constraint
// here, so there is nothing to filter client-side.
//
// It is progressive enhancement, deliberately: the input inside is a working
// text field on its own. If the script never loads, the request fails, the
// visitor is offline, or DAWA is down, they type the address by hand and the
// form submits exactly as before. Nothing here is required for the form to work.
//
// Handlers are bound once in the constructor and registered by that same
// reference, so connectedCallback can run again — a section re-render in the
// theme editor does that — without stacking duplicates. This is the same trap
// option-select.js was fixed for: `addEventListener` ignores a repeat
// registration of an identical function, but not of a fresh arrow function.

const ENDPOINT = 'https://api.dataforsyningen.dk/adresser/autocomplete';
const MIN_CHARS = 3;
// Long enough that typing a street name is one request rather than ten, short
// enough that the list feels attached to the keyboard.
const DEBOUNCE_MS = 200;
const MAX_RESULTS = 6;

class AddressAutocomplete extends HTMLElement {
  constructor() {
    super();
    this.onInput = () => this.scheduleFetch();
    this.onKeydown = (event) => this.handleKeydown(event);
    this.onFocusOut = (event) => {
      // Only close when focus actually leaves the component — moving from the
      // input to an option is still inside it.
      if (!this.contains(event.relatedTarget)) this.close();
    };
    this.onListClick = (event) => {
      const option = event.target.closest('[role="option"]');
      if (option) this.commit(Number(option.dataset.index));
    };
    // Pointer-down rather than click on the list: click fires after focusout,
    // by which point the list is already closed and the target is gone.
    this.onListPointerDown = (event) => event.preventDefault();

    this.items = [];
    this.activeIndex = -1;
    this.timer = null;
    this.controller = null;
  }

  connectedCallback() {
    this.input = this.querySelector('input');
    this.list = this.querySelector('[data-address-list]');
    this.status = this.querySelector('[data-address-status]');
    if (!this.input || !this.list) return;

    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-controls', this.list.id);
    this.input.setAttribute('autocomplete', 'off');

    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('keydown', this.onKeydown);
    this.addEventListener('focusout', this.onFocusOut);
    this.list.addEventListener('click', this.onListClick);
    this.list.addEventListener('pointerdown', this.onListPointerDown);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.controller?.abort();
  }

  scheduleFetch() {
    clearTimeout(this.timer);
    const query = this.input.value.trim();
    if (query.length < MIN_CHARS) {
      this.close();
      return;
    }

    // The list stayed open after picking an address, and this is why: `commit()`
    // dispatches an `input` event so the calculator recalculates — and this
    // handler is listening for exactly that event, so it looked them up again
    // and reopened the panel on the address just chosen. Comparing against the
    // committed value stops that, and also stops a pointless lookup when the
    // visitor clicks back into a field they have already filled.
    if (query === this.committedValue) {
      this.close();
      return;
    }

    this.timer = setTimeout(() => this.fetchSuggestions(query), DEBOUNCE_MS);
  }

  async fetchSuggestions(query) {
    // Abort the previous request: without this a slow early response can land
    // after a fast later one and repopulate the list with stale addresses.
    this.controller?.abort();
    this.controller = new AbortController();

    const url = `${ENDPOINT}?q=${encodeURIComponent(query)}&per_side=${MAX_RESULTS}&type=adresse`;
    try {
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      this.items = (Array.isArray(data) ? data : [])
        .map((entry) => entry?.tekst)
        .filter((text) => typeof text === 'string' && text.length > 0);
      this.render();
    } catch (error) {
      if (error.name === 'AbortError') return;
      // A failed lookup must never block the field. Close the list and leave the
      // visitor typing.
      this.items = [];
      this.close();
    }
  }

  render() {
    if (this.items.length === 0) {
      this.close();
      return;
    }

    this.activeIndex = -1;
    this.list.replaceChildren(
      ...this.items.map((text, index) => {
        const li = document.createElement('li');
        li.id = `${this.list.id}-option-${index}`;
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        li.dataset.index = String(index);
        li.className = 'address-autocomplete__option';
        li.textContent = text;
        return li;
      })
    );

    this.open();
    this.announce(this.items.length);
  }

  announce(count) {
    if (!this.status) return;
    const word = count === 1 ? 'adresse' : 'adresser';
    this.status.textContent = `${count} ${word} fundet. Brug piletasterne for at vælge.`;
  }

  open() {
    this.list.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    clearTimeout(this.timer);
    this.list.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
    if (this.status) this.status.textContent = '';
  }

  setActive(index) {
    const options = [...this.list.children];
    if (options.length === 0) return;
    // Wraps at both ends, so ArrowUp from the input jumps to the last address.
    this.activeIndex = (index + options.length) % options.length;
    options.forEach((option, i) => {
      const active = i === this.activeIndex;
      option.setAttribute('aria-selected', active ? 'true' : 'false');
      option.classList.toggle('address-autocomplete__option--active', active);
      if (active) {
        this.input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  commit(index) {
    const text = this.items[index];
    if (typeof text !== 'string') return;
    this.input.value = text;
    // Recorded BEFORE the events below, because they re-enter scheduleFetch()
    // and this is what tells it to leave the panel shut.
    this.committedValue = text;
    // Nothing is left to choose from, so the list must not be able to reopen
    // from a stale response either.
    this.items = [];
    clearTimeout(this.timer);
    this.controller?.abort();
    this.close();
    // Both events: `input` is what the calculator listens to for its live
    // recalculation, `change` is what a plain form validator would use.
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.input.focus();
  }

  handleKeydown(event) {
    const isOpen = !this.list.hidden;

    if (event.key === 'Escape') {
      if (isOpen) {
        event.preventDefault();
        this.close();
      }
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!isOpen) return;
      event.preventDefault();
      this.setActive(this.activeIndex + (event.key === 'ArrowDown' ? 1 : -1));
      return;
    }

    // Enter only commits when an address is actually highlighted. Otherwise it
    // falls through to the form, so a visitor who typed a full address and hit
    // Enter is not silently blocked.
    if (event.key === 'Enter' && isOpen && this.activeIndex >= 0) {
      event.preventDefault();
      this.commit(this.activeIndex);
    }
  }
}

if (!customElements.get('address-autocomplete')) {
  customElements.define('address-autocomplete', AddressAutocomplete);
}
